import React, { useMemo, useRef, useState } from 'react'
import { useAtomValueLoadable } from 'src/utils/jotai'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import styled from '@emotion/styled'
import { Close } from '@emotion-icons/material/Close'
import { Colors, Shapes } from 'src/components/theme'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import { Divider, IconButton, Typography } from '@mui/material'
import { AMOwnershipEntitySearchResult } from 'src/common-services'
import { ResultItem, SidebarResultListItem } from 'src/v2/components/sidebar-result-list'
import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { formatString } from 'src/utils/format-string'
import { EmptyResultItem } from 'src/views/map-explorer/sidebar/panels/results/empty-result-item'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { SidebarResultOverlay } from 'src/v2/components/sidebar-result-overlay'
import { SidebarResultsSkeletonLoaderItem } from 'src/views/map-explorer/sidebar/panels/loader'
import { Person } from '@emotion-icons/material/Person'
import { MapSquareIcon } from 'src/components/icons/map-square'
import { prettyNumber } from 'src/utils/number-format'

import * as atoms from 'src/v2/state/owner-reveal/parcel'

const buildOwnerResults = (owners: AMOwnershipEntitySearchResult[]): SidebarResultListItem[] => {
  return owners.map((owner) => ({
    id: owner.id,
    title: formatString(owner.name, 'name'),
    subtitle: (
      <FlexRow align="center" gap={Shapes.spacing[2]}>
        <FlexRow align="center" gap={Shapes.spacing[1]}>
          <Person size={16} color={Colors.neutral300} />
          <Typography variant="text1m" color={Colors.neutral300}>
            {prettyNumber(owner.amountOfContacts)}
          </Typography>
        </FlexRow>
        <Divider orientation="vertical" flexItem />
        <FlexRow align="center" gap={Shapes.spacing[1]}>
          <MapSquareIcon size={12} color={Colors.neutral300} />
          <Typography variant="text1m" color={Colors.neutral300}>
            {prettyNumber(owner.amountOfParcels)}
          </Typography>
        </FlexRow>
      </FlexRow>
    ),
    icon: <Briefcase size={16} />,
    color: Colors.primaryRed700,
  }))
}

const ResultsRow = styled(FlexRow)`
  flex: 1;
  gap: ${Shapes.spacing[2]}px;
  flex-wrap: wrap;
`

type ParcelOwnersContentProps = {
  open: boolean
  onClose: () => void
  parentRef: React.RefObject<HTMLDivElement>
  owners: AMOwnershipEntitySearchResult[]
}
const ParcelOwnersContent: React.FC<ParcelOwnersContentProps> = ({ open, onClose, parentRef, owners }) => {
  const { selectOwner } = useMapExplorerNavigation()

  const results = useMemo(() => {
    if (!owners) {
      return undefined
    }
    return buildOwnerResults(owners)
  }, [owners])

  if (!results) {
    return <EmptyResultItem title="Owner" />
  }
  return (
    <>
      <FlexColumn gap={Shapes.spacing[1]}>
        {results.slice(0, 3).map((owner) => (
          <ResultItem key={owner.id} item={owner} onClick={() => selectOwner(owner.id)} mini maxWidth={250} />
        ))}
      </FlexColumn>
      <SidebarResultOverlay
        open={open}
        anchorEl={parentRef.current}
        onClose={onClose}
        maxWidth={owners.length > 10 ? 650 : 322}
        contentPadding={Shapes.spacing[2]}>
        <FlexColumn gap={Shapes.spacing[1]}>
          <FlexRow justify="space-between" align="center">
            <Typography variant="textmd" color={Colors.neutral700}>
              Owners ({results.length})
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <Close size={16} />
            </IconButton>
          </FlexRow>
          <ResultsRow>
            {results.map((owner) => (
              <ResultItem
                key={owner.id}
                item={owner}
                onClick={() => selectOwner(owner.id)}
                mini={owners.length > 10}
                maxWidth={250}
              />
            ))}
          </ResultsRow>
        </FlexColumn>
      </SidebarResultOverlay>
    </>
  )
}

const ParcelOwnersWrapper = styled(FlexColumn)`
  flex: 0;
  gap: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[5]}px ${Shapes.spacing[4]}px;
`

export const ParcelOwners: React.FC = () => {
  const owners = useAtomValueLoadable(atoms.parcelOwners)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const onToggleOpen = () => {
    setOpen((prev) => !prev)
  }

  const onClose = () => {
    setOpen(false)
  }
  return (
    <ParcelOwnersWrapper ref={ref}>
      <FlexRow justify="space-between" align="center">
        <Typography variant="title2" color={Colors.neutral950}>
          Owners {owners.state === 'hasData' && owners.data?.length ? `(${owners.data.length})` : ''}
        </Typography>
        {owners.state === 'hasData' && owners.data?.length && owners.data.length > 3 && (
          <ButtonWithIcon onClick={onToggleOpen}>View {open ? 'Less' : 'All'}</ButtonWithIcon>
        )}
      </FlexRow>
      {owners.state === 'loading' ? (
        <FlexColumn gap={Shapes.spacing[1]}>
          <SidebarResultsSkeletonLoaderItem />
          <SidebarResultsSkeletonLoaderItem />
        </FlexColumn>
      ) : owners.state === 'hasData' && owners.data ? (
        <ParcelOwnersContent parentRef={ref} open={open} onClose={onClose} owners={owners.data} />
      ) : null}
    </ParcelOwnersWrapper>
  )
}
